import type { TileType } from './types';

/**
 * Stub interface for tile landing effects.
 * Full implementation deferred to Plan 3a (game state + turns).
 * For Plan 2, `onLand` is a no-op stub that returns an empty array.
 *
 * Spec §3 tile types table defines permanent tile types with distinct landing effects.
 * Spec §3: "Landing vs passing: Special tiles trigger ONLY on landing (final tile)."
 */
export interface TileDefinition {
  type: TileType;
  name: string;
  description: string;
  /**
   * Whether this tile can be landed on.
   * Spec §3: pathing tiles are "Not landable" — players pass through without consuming a step.
   */
  landable: boolean;
  /**
   * Stub: called when a player lands on this tile.
   * Returns an array of state change descriptors (empty in Plan 2 stubs).
   * Full implementation in Plan 3a when GameState is available.
   */
  onLand: () => string[];
}

/**
 * Internal mutable registry. Exported as ReadonlyMap via `tileRegistry`.
 * Follows the registry pattern from src/shared/CLAUDE.md.
 */
const _tileRegistry = new Map<TileType, TileDefinition>([
  [
    'start',
    {
      type: 'start',
      name: 'Start',
      description: 'Spawn point. No effect during play.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'gold',
    {
      type: 'gold',
      name: 'Gold',
      description: '+5 keys on landing.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'heal',
    {
      type: 'heal',
      name: 'Heal',
      description: '+15 HP on landing.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'hazard',
    {
      type: 'hazard',
      name: 'Hazard',
      description: '-5 HP + proportional key drop on landing.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'item',
    {
      type: 'item',
      name: 'Item',
      description: 'Grants a random standard item on landing.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'soul_anchor',
    {
      type: 'soul_anchor',
      name: 'Soul Anchor',
      description: 'First lander claims: steal 6 keys OR 9-12 HP from future landers.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'key_shop',
    {
      type: 'key_shop',
      name: 'Key Shop',
      description: 'Shop tile for key economy interactions.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'weapons_cache',
    {
      type: 'weapons_cache',
      name: 'Weapons Cache',
      description: 'Dynamic armory cache location.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'mystery_box',
    {
      type: 'mystery_box',
      name: 'Mystery Box',
      description: 'Mystery reward tile.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'event',
    {
      type: 'event',
      name: 'Event',
      description: 'Map-specific event affecting all players.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'graveyard',
    {
      type: 'graveyard',
      name: 'Graveyard',
      description: 'Respawn point. Furthest from treasure chest.',
      landable: true,
      onLand: () => [],
    },
  ],
  [
    'pathing',
    {
      type: 'pathing',
      name: 'Pathing',
      description: 'Invisible routing node. Players pass through without consuming a step.',
      landable: false,
      onLand: () => [],
    },
  ],
]);

/**
 * Registry mapping TileType to TileDefinition.
 * Follows the registry pattern from src/shared/CLAUDE.md.
 */
export const tileRegistry: ReadonlyMap<TileType, TileDefinition> = _tileRegistry;

/**
 * Get a tile definition by type. Throws if tile type is not registered.
 */
export function getTileDefinition(type: TileType): TileDefinition {
  const def = _tileRegistry.get(type);
  if (!def) {
    throw new Error(`Unknown tile type: ${type}`);
  }
  return def;
}

/**
 * Get all registered tile types.
 */
export function getAllTileTypes(): TileType[] {
  return Array.from(_tileRegistry.keys());
}

/**
 * Returns true if the given tile type is landable (player can end movement on it).
 * Spec §3: pathing tiles are "Not landable."
 */
export function isLandable(type: TileType): boolean {
  return getTileDefinition(type).landable;
}

/**
 * Returns true if the given tile type is a passthrough tile (player passes without consuming a step).
 * Currently only 'pathing' is passthrough. This function exists for future extensibility.
 */
export function isTilePassthrough(type: TileType): boolean {
  return type === 'pathing';
}
